import React, { useState, useEffect } from 'react';
import { 
  Gauge, Edit2, Save, X, RefreshCw, Loader,
  Info, AlertCircle, CheckCircle
} from 'lucide-react';
import { vehicleProfileAPI } from '../services/api';
import logger from '../utils/logger';

const MileageUpdateComponent = ({ 
  vehicleId, 
  currentMileage = 0, 
  onMileageUpdate = null,
  showUnitToggle = true,
  className = ""
}) => {
  const [editing, setEditing] = useState(false);
  const [tempMileage, setTempMileage] = useState(currentMileage);
  const [unit, setUnit] = useState('miles'); // 'miles' or 'km'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setTempMileage(currentMileage);
  }, [currentMileage]);

  const convertMileage = (value, fromUnit, toUnit) => {
    if (fromUnit === toUnit) return value;
    if (fromUnit === 'miles' && toUnit === 'km') {
      return Math.round(value * 1.60934);
    } else if (fromUnit === 'km' && toUnit === 'miles') {
      return Math.round(value / 1.60934);
    }
    return value;
  };

  const handleUnitChange = (newUnit) => {
    if (newUnit !== unit) {
      const convertedValue = convertMileage(tempMileage, unit, newUnit);
      setTempMileage(convertedValue);
      setUnit(newUnit);
    }
  };

  const handleSave = async () => {
    if (tempMileage < 0) {
      setError('Mileage cannot be negative');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Convert to miles for storage (backend stores in miles)
      const mileageInMiles = unit === 'km' ? convertMileage(tempMileage, 'km', 'miles') : tempMileage;
      
      await vehicleProfileAPI.updateMileage(vehicleId, mileageInMiles);
      
      setSuccess(true);
      setEditing(false);
      
      // Call parent callback if provided
      if (onMileageUpdate) {
        onMileageUpdate(mileageInMiles);
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error('Failed to update mileage:', err);
      setError(err.response?.data?.detail || 'Failed to update mileage. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setTempMileage(currentMileage);
    setError(null);
    setUnit('miles');
  };

  const formatMileage = (value, unit) => {
    return `${value.toLocaleString()} ${unit}`;
  };

  const displayMileage = unit === 'km' 
    ? convertMileage(currentMileage, 'miles', 'km')
    : currentMileage;

  logger.log('MileageUpdateComponent render:', { vehicleId, currentMileage, editing, unit });
  
  return (
    <div className={`bg-surface-card rounded-card shadow-elevation-1 border p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Gauge className="w-5 h-5 text-accent" />
          <h3 className="font-semibold text-ink-soft">Current Mileage</h3>
        </div>
        {showUnitToggle && !editing && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleUnitChange('miles')}
              className={`px-2 py-1 text-small rounded transition-colors ${
                unit === 'miles' 
                  ? 'bg-accent/10 text-accent font-medium' 
                  : 'bg-surface-muted text-ink-muted hover:bg-surface-muted'
              }`}
            >
              Miles
            </button>
            <button
              onClick={() => handleUnitChange('km')}
              className={`px-2 py-1 text-small rounded transition-colors ${
                unit === 'km' 
                  ? 'bg-accent/10 text-accent font-medium' 
                  : 'bg-surface-muted text-ink-muted hover:bg-surface-muted'
              }`}
            >
              Kilometers
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <input
              type="number"
              value={tempMileage}
              onChange={(e) => setTempMileage(parseInt(e.target.value) || 0)}
              className="flex-1 px-3 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
              placeholder={`Enter mileage in ${unit}`}
              min="0"
              autoFocus
            />
            <span className="text-sm text-ink-muted font-medium min-w-[60px]">
              {unit}
            </span>
          </div>

          {showUnitToggle && (
            <div className="flex items-center gap-2">
              <span className="text-small text-ink-muted">Unit:</span>
              <button
                onClick={() => handleUnitChange('miles')}
                className={`px-2 py-1 text-small rounded transition-colors ${
                  unit === 'miles' 
                    ? 'bg-accent/10 text-accent font-medium' 
                    : 'bg-surface-muted text-ink-muted hover:bg-surface-muted'
                }`}
              >
                Miles
              </button>
              <button
                onClick={() => handleUnitChange('km')}
                className={`px-2 py-1 text-small rounded transition-colors ${
                  unit === 'km' 
                    ? 'bg-accent/10 text-accent font-medium' 
                    : 'bg-surface-muted text-ink-muted hover:bg-surface-muted'
                }`}
              >
                Kilometers
              </button>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-danger text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 text-success text-sm">
              <CheckCircle className="w-4 h-4" />
              Mileage updated successfully!
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <Loader className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
            <button
              onClick={handleCancel}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 bg-surface-muted text-ink-soft rounded-lg hover:bg-surface-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl font-bold text-ink-soft">
              {formatMileage(displayMileage, unit)}
            </span>
            {unit === 'km' && (
              <span className="text-sm text-ink-muted">
                ({formatMileage(currentMileage, 'miles')})
              </span>
            )}
          </div>
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-2 px-3 py-2 text-accent hover:bg-surface-page rounded-lg transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            Update
          </button>
        </div>
      )}

      {!editing && (
        <div className="mt-3 pt-3 border-t border-border-default">
          <div className="flex items-center gap-2 text-small text-ink-muted">
            <Info className="w-3 h-3" />
            <span>
              Last updated: {new Date().toLocaleDateString()}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MileageUpdateComponent;
